import { useState } from 'react';
import { creatorbase } from '@round/api';
import { buildYoutubeChannelUrl, showNotification } from 'helpers';
import useRefreshProjectCampaignStats from '../../../hooks/useRefreshProjectCampaignStats';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import { CampaignTableRow } from '../../ProjectCampaignsTable/ProjectCampaignsTable';
import BackupCreatorsExpandableBar from '../../BackupCreatorsExpandableBar/BackupCreatorsExpandableBar';
import YoutubePostsTable from './YoutubePostsTable';
import useProjectDetailsYoutubePosts, {
    YoutubePostsTableRow,
} from '../../../hooks/posts/useProjectDetailsYoutubePosts';
import useUpdateProjectDetailsPost from '../../../hooks/posts/useUpdateProjectDetailsPost';
import DeletePostModal from '../../../Posts/DeletePostModal/DeletePostModal';
import PostStatsModal, { PostStatsModalAccount } from 'Modules/Plans/Posts/components/PostStatsModal/PostStatsModal';
import { useInvalidatePosts } from '@round/creatorbase-helpers/api-hooks';
import { getProjectDetailsPostsParams } from '../../../hooks/posts/getProjectDetailsPostsParams';

type Props = {
    campaign: CampaignTableRow;
    isBackupExpanded: boolean;
    onBackupExpandToggle: () => void;
    isPaymentEnabled?: boolean;
    onCreatePost: () => void;
};

const isBackupPost = (post: YoutubePostsTableRow) =>
    backupPostsStatuses.includes(post.status as creatorbase.BackupPostStatus);

export default function YoutubePosts({
    campaign,
    isBackupExpanded,
    onBackupExpandToggle,
    isPaymentEnabled,
    onCreatePost,
}: Props) {
    const { data, isLoading, hasError, isAccountDataLoading } = useProjectDetailsYoutubePosts(campaign.id);

    const refreshAllStats = useRefreshProjectCampaignStats();
    const { updatePost } = useUpdateProjectDetailsPost(campaign.id, 'youtube');
    const invalidatePosts = useInvalidatePosts();
    // A status change moves a post between the active and backup tables, so
    // refresh the campaign's posts when the status cell reports an update. It
    // also shifts the post in/out of the active set the headline and
    // campaign-row stats are aggregated from, so refresh those too.
    const handlePostStatusUpdated = () => {
        invalidatePosts(getProjectDetailsPostsParams(campaign.id));
        refreshAllStats(campaign.id, 'youtube').catch(() => {
            showNotification('Could not refresh data', 'error');
        });
    };

    // A single fetch returns every post for the campaign; the active table shows
    // everything that isn't a backup status, the backup bar shows the rest. The
    // status lists are exhaustive over manual statuses, so no post is dropped.
    const activePosts = data.filter((post) => !isBackupPost(post));
    const backupPosts = data.filter(isBackupPost);

    const [postToDelete, setPostToDelete] = useState<YoutubePostsTableRow | null>(null);
    // Kept as an id so the open modal follows the refetched post and its account as they load
    const [statsPostId, setStatsPostId] = useState<YoutubePostsTableRow['id'] | null>(null);
    const statsPost = data.find((post) => post.id === statsPostId);

    return (
        <>
            <YoutubePostsTable
                isPaymentEnabled={isPaymentEnabled}
                data={activePosts}
                campaign={campaign}
                isLoading={isLoading}
                isAccountDataLoading={isAccountDataLoading}
                hasError={hasError}
                onCreatePost={onCreatePost}
                onDeletePost={(post) => setPostToDelete(post)}
                onOpenPostStatsModal={(post) => setStatsPostId(post.id)}
                updatePost={updatePost}
                onPostStatusUpdated={handlePostStatusUpdated}
            />

            {campaign.managed_by === 'creatorbase' && (
                <BackupCreatorsExpandableBar
                    isExpanded={isBackupExpanded}
                    onExpandToggle={onBackupExpandToggle}
                    backupCreatorsCount={backupPosts.length}
                >
                    <YoutubePostsTable
                        isPaymentEnabled={isPaymentEnabled}
                        data={backupPosts}
                        campaign={campaign}
                        isLoading={isLoading}
                        isAccountDataLoading={isAccountDataLoading}
                        hasError={hasError}
                        onDeletePost={(post) => setPostToDelete(post)}
                        onOpenPostStatsModal={(post) => setStatsPostId(post.id)}
                        updatePost={updatePost}
                        onPostStatusUpdated={handlePostStatusUpdated}
                    />
                </BackupCreatorsExpandableBar>
            )}

            <DeletePostModal isOpen={!!postToDelete} post={postToDelete} onClose={() => setPostToDelete(null)} />

            {statsPost && (
                <PostStatsModal
                    isOpen
                    post={statsPost}
                    account={getPostStatsModalAccount(statsPost, isAccountDataLoading)}
                    onClose={() => setStatsPostId(null)}
                />
            )}
        </>
    );
}

const getPostStatsModalAccount = (
    post: YoutubePostsTableRow,
    isAccountDataLoading: boolean
): PostStatsModalAccount | null => {
    if (!post.youtube_details?.account_id) {
        return null;
    }

    if (isAccountDataLoading) {
        return { isLoading: true };
    }

    if (!post.channel) {
        return null;
    }

    return {
        name: post.channel.title,
        image: post.channel.thumbnail,
        profileUrl: buildYoutubeChannelUrl(post.channel.youtube_id),
    };
};
